import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Clock, Play } from 'lucide-react';

export const StartDialog = ({ open, onOpenChange, onStart }) => {
  const [minutes, setMinutes] = useState(5);
  const [seconds, setSeconds] = useState(0);

  const handleStart = () => {
    const totalSeconds = minutes * 60 + seconds;
    onStart(totalSeconds);
    onOpenChange(false);
  };

  const handleMinutesChange = (value) => {
    const newMinutes = parseInt(value) || 0;
    setMinutes(Math.max(0, newMinutes));
  };

  const handleSecondsChange = (value) => {
    const newSeconds = parseInt(value) || 0;
    setSeconds(Math.max(0, Math.min(59, newSeconds)));
  };

  const formatTime = (mins, secs) => {
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const totalSeconds = minutes * 60 + seconds;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-gray-200 shadow-xl max-w-md">
        <DialogHeader className="pb-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock className="size-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-xl text-gray-900 font-semibold">Start Competition Round</DialogTitle>
              <DialogDescription className="text-gray-600 mt-1">
                Set the countdown time before the round begins
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-6 py-6">
          {/* Time Input Section */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <Label htmlFor="minutes" className="text-gray-700 font-medium text-sm">
                  Minutes
                </Label>
                <Input
                  id="minutes"
                  type="number"
                  min={0}
                  max={59}
                  value={minutes}
                  onChange={(e) => handleMinutesChange(e.target.value)}
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11 text-center text-lg font-semibold"
                  placeholder="00"
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="seconds" className="text-gray-700 font-medium text-sm">
                  Seconds
                </Label>
                <Input
                  id="seconds"
                  type="number"
                  min={0}
                  max={59}
                  value={seconds}
                  onChange={(e) => handleSecondsChange(e.target.value)}
                  className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11 text-center text-lg font-semibold"
                  placeholder="00"
                />
              </div>
            </div>
            
            {/* Quick Preset Buttons */}
            <div className="flex gap-2 justify-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setMinutes(1); setSeconds(0); }}
                className="text-xs border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                1:00
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setMinutes(3); setSeconds(0); }}
                className="text-xs border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                3:00
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setMinutes(5); setSeconds(0); }}
                className="text-xs border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                5:00
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setMinutes(10); setSeconds(0); }}
                className="text-xs border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                10:00
              </Button>
            </div>
          </div>
          
          {/* Preview Section */}
          <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
            <p className="text-sm text-blue-600 font-medium mb-2">Countdown Preview</p>
            <div className="flex items-center justify-center gap-3">
              <Clock className="size-6 text-blue-600" />
              <p className="text-4xl text-blue-700 tabular-nums font-bold">
                {formatTime(minutes, seconds)}
              </p>
            </div>
            <p className="text-xs text-blue-600 mt-2">
              {totalSeconds} second{totalSeconds !== 1 ? 's' : ''} total
            </p>
          </div>

          {/* Status Message */}
          {totalSeconds === 0 && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-700 text-center">
                Countdown will start immediately
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="pt-4 border-t border-gray-200">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            className="border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleStart}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium"
            disabled={totalSeconds < 0}
          >
            <Play className="size-4 mr-2" />
            Start Countdown
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};